export interface ParsedInviteToken {
  orgId: string;
  email: string;
  /** The original base64 value passed directly to PropelAuth's signup API. */
  rawToken: string;
}

/**
 * Decodes a PropelAuth invite token from a URL query parameter.
 *
 * PropelAuth encodes the invite data as a base64 JSON string:
 *   {"org_id":"...","email":"...","token":"..."}
 *
 * Returns null if the token is missing or malformed.
 */
export const parseInviteToken = (raw: string | null | undefined): ParsedInviteToken | null => {
  if (!raw) return null;
  try {
    const decoded = JSON.parse(atob(raw));
    if (
      decoded &&
      typeof decoded.org_id === 'string' &&
      typeof decoded.email === 'string' &&
      typeof decoded.token === 'string'
    ) {
      return {orgId: decoded.org_id, email: decoded.email, rawToken: raw};
    }
  } catch (_) {
    /* malformed — ignore */
  }
  return null;
};

/**
 * Extracts an invite token from a route's query params. Handles two cases:
 *   1. Direct:  ?invite_token=BASE64
 *   2. Nested:  ?redirect=/?invite_token=BASE64&e=BASE64  (common when middleware
 *      preserves the invite URL through the login redirect).
 */
export const extractInviteTokenFromQuery = (query: Record<string, string | string[]>): ParsedInviteToken | null => {
  // Direct param
  const direct = typeof query.invite_token === 'string' ? query.invite_token : null;
  if (direct) return parseInviteToken(direct);

  // Nested inside `redirect`
  const redirect = typeof query.redirect === 'string' ? query.redirect : null;
  if (redirect) {
    try {
      const url = new URL(redirect, 'http://localhost');
      const nested = url.searchParams.get('invite_token');
      if (nested) return parseInviteToken(nested);
    } catch (_) { /* malformed URL — ignore */ }
  }

  return null;
};
